import { useEffect, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { showNotification } from "@mantine/notifications";
import { Button } from "@mantine/core";
import { IconRefresh } from "@tabler/icons";
import { hardReloadClient, isReloadInProgress } from "../utils/clientReload";
import {
  isClientUpdateAvailable,
  isTrustedRemoteVersion,
} from "../utils/versionCompare";
import { hasUnsavedClientWork } from "../context/tabContext";

const POLL_INTERVAL_MS = 60 * 1000;
const CURRENT_VERSION = process.env.REACT_APP_VERSION;
const CURRENT_BUILD_ID = process.env.REACT_APP_BUILD_ID;
const IS_DEV = process.env.NODE_ENV !== "production";

let snapshot = {
  updateAvailable: false,
  latestVersion: null,
  latestBuildId: null,
  autoReloadBlocked: false,
};

const listeners = new Set();
let pollTimer = null;
let subscriberCount = 0;

function emit() {
  listeners.forEach((listener) => listener());
}

function setSnapshot(next) {
  snapshot = next;
  emit();
}

export function applyRemoteClientBuild(data) {
  if (IS_DEV || !data) return;

  const latest = data.version;
  const remoteBuildId = data.buildId || null;
  const updateAvailable = isClientUpdateAvailable({
    remoteVersion: latest,
    remoteBuildId,
    currentVersion: CURRENT_VERSION,
    currentBuildId: CURRENT_BUILD_ID,
  });

  let autoReloadBlocked = false;
  if (updateAvailable && !hasUnsavedClientWork()) {
    const identity = remoteBuildId || latest;
    const started = hardReloadClient({ buildId: identity });
    autoReloadBlocked = !started && !isReloadInProgress(identity);
  }

  setSnapshot({
    updateAvailable,
    latestVersion: isTrustedRemoteVersion(latest) ? latest : null,
    latestBuildId: remoteBuildId,
    autoReloadBlocked,
  });
}

async function checkVersion() {
  if (IS_DEV || document.hidden) return;

  try {
    const base = process.env.PUBLIC_URL || "";
    const res = await fetch(`${base}/version.json?ts=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return;
    applyRemoteClientBuild(await res.json());
  } catch {
    // Silent failure — do not disturb users on network errors.
  }
}

function stopPolling() {
  if (!pollTimer) return;
  window.clearInterval(pollTimer);
  pollTimer = null;
}

function syncPolling() {
  if (IS_DEV || subscriberCount === 0 || document.hidden) {
    stopPolling();
    return;
  }

  checkVersion();
  if (pollTimer) return;
  pollTimer = window.setInterval(() => {
    if (document.hidden || subscriberCount === 0) return;
    checkVersion();
  }, POLL_INTERVAL_MS);
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return snapshot;
}

export function bindClientBuildSocket(socket) {
  if (!socket || socket.__mbidClientBuildBound) return;
  socket.__mbidClientBuildBound = true;
  socket.on("clientBuild", (payload) => {
    applyRemoteClientBuild(payload);
  });
}

export function useClientVersionCheck({ notify = false } = {}) {
  const { t, ready } = useTranslation();
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    subscriberCount += 1;
    syncPolling();

    const onVisible = () => syncPolling();
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      subscriberCount -= 1;
      document.removeEventListener("visibilitychange", onVisible);
      syncPolling();
    };
  }, []);

  useEffect(() => {
    if (!ready || !notify || !state.updateAvailable) return;

    const needsBanner = state.autoReloadBlocked || hasUnsavedClientWork();
    if (!needsBanner) return;

    showNotification({
      id: "client-update-available",
      title: t("app.updateAvailable.title", {
        defaultValue: "Update available",
      }),
      message: (
        <>
          <div style={{ marginBottom: 8 }}>
            {t(
              state.autoReloadBlocked
                ? "app.updateAvailable.blocked"
                : "app.updateAvailable.message",
              {
                latest: state.latestVersion || state.latestBuildId,
                current: CURRENT_VERSION,
                defaultValue: state.autoReloadBlocked
                  ? "A new version is ready, but the page did not update. Reload now to use it."
                  : "A new version is ready. It will load after you save, or you can reload now.",
              }
            )}
          </div>
          <Button
            size="xs"
            leftIcon={<IconRefresh size={14} />}
            onClick={() =>
              hardReloadClient({
                buildId: state.latestBuildId || state.latestVersion,
                force: true,
              })
            }
          >
            {t("app.updateAvailable.reload", {
              defaultValue: "Reload now",
            })}
          </Button>
        </>
      ),
      autoClose: false,
      color: "orange",
      withCloseButton: false,
    });
  }, [
    ready,
    notify,
    state.updateAvailable,
    state.autoReloadBlocked,
    state.latestBuildId,
    state.latestVersion,
    t,
  ]);

  return {
    updateAvailable: state.updateAvailable,
    latestVersion: state.latestVersion,
    reload: () =>
      hardReloadClient({
        buildId: getSnapshot().latestBuildId || getSnapshot().latestVersion,
        force: true,
      }),
  };
}
